import React, { useState, useEffect } from "react";
import { useSnackbar } from './context/SnackbarContext';
import { useNavigate } from "react-router-dom";
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Box,
    CircularProgress,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Switch,
    FormControlLabel
} from "@mui/material";
import { Edit, Delete, Launch } from "@mui/icons-material";
import { blueGrey } from "@mui/material/colors";
import { project_api_path } from "./api_registry";


// Change this to your backend base URL

export default function ProjectsPage({ page_state, project_state }) {

    const [projectToBeLounched, setProjectToBeLounched] = project_state;
    const [page, setPage] = page_state;
    const [projects, setProjects] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);
    const [newProject, setNewProject] = useState({ name: "", description: "", default_layers: true });

    const [editOpen, setEditOpen] = useState(false);
    const [editProject, setEditProject] = useState({
        id: null,
        name: "",
        description: "",
    });

    const [deleteId, setDeleteId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const navigate = useNavigate();

    // for message 
    const { showSnackbar } = useSnackbar();

    // --- Fetch Projects ---
    const fetchProjects = async (pageNum) => {
        setLoading(true);
        try {
            const res = await fetch(
                `${project_api_path}/?page=${pageNum}&page_size=${pageSize}`
            );
            if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
            const data = await res.json();
            const n_pages = Math.ceil(data.total / pageSize);
            setProjects(data.items);
            setTotalPages(n_pages);
            setPageSize(10);
            showSnackbar(`Loaded ${data.total} projects`, 'success');
        } catch (err) {
            showSnackbar(`Error loading projects : ${err}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects(page);
    }, [page]);

    // --- Create Project ---
    const handleCreate = async () => {
        setActionLoading(true);
        try {
            const res = await fetch(`${project_api_path}/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newProject),
            });
            if (!res.ok) throw new Error(`Create failed: ${res.status}`);
            await res.json();
            await fetchProjects(page);
            setCreateOpen(false);
            setNewProject({ name: "", description: "", default_layers: true });
            showSnackbar(`project (name=${newProject.name}) created successfully.`, 'success')
        } catch (err) {
            showSnackbar(`Error creating project: ${err}`, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // --- Edit Project ---
    const handleEdit = async () => {
        setActionLoading(true);
        try {
            const response = await fetch(
                `${project_api_path}/${editProject.id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(editProject),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update project: ${errorText}`);
            }

            const updated = await response.json();
            setProjects((prev) =>
                prev.map((p) => (p.id === updated.id ? updated : p))
            );
            setEditOpen(false);
            showSnackbar(`project (id=${updated.id}) updated successfully.`, 'success');
        } catch (err) {
            showSnackbar(err.message, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // --- Delete Project ---
    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this project?")) return;
        setActionLoading(true);
        try {
            const response = await fetch(`${project_api_path}/${deleteId}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to delete project: ${errorText}`);
            }

            setProjects((prev) => prev.filter((p) => p.id !== deleteId));
            setDeleteId(null);
            showSnackbar(`project (id=${deleteId}) deleted successfully.`, 'success');
        } catch (err) {
            showSnackbar(err.message, 'error');
            alert(err.message || "Failed to delete project");
        } finally {
            setActionLoading(false);
        }
    };

    // --- Loading Spinner ---
    if (loading)
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );

    return (
        <Container
            sx={{
                p: 4,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                fontFamily: "'Roboto', sans-serif",
                color: blueGrey[900],
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    mb: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: blueGrey[800] }}
                >
                    Projects
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setCreateOpen(true)}
                >
                    Create Project
                </Button>
            </Box>

            {/* Table */}
            <TableContainer
                component={Paper}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    mb: 2,
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>SL</TableCell>
                            <TableCell>Project Name</TableCell>
                            <TableCell>About</TableCell>
                            <TableCell align="center">Edit</TableCell>
                            <TableCell align="center">Delete</TableCell>
                            <TableCell align="center">Launch</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {projects.map((project, index) => (
                            <TableRow key={project.id} hover>
                                <TableCell>
                                    {(page - 1) * pageSize + index + 1}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    {project.name}
                                </TableCell>
                                <TableCell>{project.description}</TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        color="primary"
                                        onClick={() => {
                                            setEditProject(project);
                                            setEditOpen(true);
                                        }}
                                    >
                                        <Edit />
                                    </IconButton>
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        color="error"
                                        onClick={() => setDeleteId(project.id)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        color="success"
                                        onClick={() => {
                                            setLoading(true);
                                            setProjectToBeLounched(project);
                                            setTimeout(() => {
                                                navigate("/route-planner");
                                                setLoading(false);
                                            }, 100);
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <CircularProgress
                                                size={20}
                                                color="success"
                                            />
                                        ) : (
                                            <Launch />
                                        )}
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                />
            </Box>

            {/* Create Dialog */}
            <Dialog
                open={createOpen}
                onClose={() => !actionLoading && setCreateOpen(false)}
                disableRestoreFocus
                disableEnforceFocus
                keepMounted
            >
                <DialogTitle>Create Project</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Project Name"
                        value={newProject.name}
                        onChange={(e) =>
                            setNewProject({
                                ...newProject,
                                name: e.target.value,
                            })
                        }
                        disabled={actionLoading}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="About"
                        multiline
                        minRows={3}
                        value={newProject.description}
                        onChange={(e) =>
                            setNewProject({
                                ...newProject,
                                description: e.target.value,
                            })
                        }
                        disabled={actionLoading}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={newProject.default_layers}
                                onChange={(e) =>
                                    setNewProject({ ...newProject, default_layers: e.target.checked })
                                }
                            />
                        }
                        label="Add default (POP, OFC, LANDMARK, CROSSING ..) layers"
                    />

                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateOpen(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={actionLoading}
                    >
                        {actionLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            "Create"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={editOpen}
                onClose={() => !actionLoading && setEditOpen(false)}
                disableRestoreFocus
                disableEnforceFocus
                keepMounted
            >
                <DialogTitle>Edit Project</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Project Name"
                        value={editProject.name}
                        onChange={(e) =>
                            setEditProject({
                                ...editProject,
                                name: e.target.value,
                            })
                        }
                        disabled={actionLoading}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="About"
                        multiline
                        minRows={3}
                        value={editProject.description}
                        onChange={(e) =>
                            setEditProject({
                                ...editProject,
                                description: e.target.value,
                            })
                        }
                        disabled={actionLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setEditOpen(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleEdit}
                        disabled={actionLoading}
                    >
                        {actionLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={Boolean(deleteId)}
                onClose={() => !actionLoading && setDeleteId(null)}
                disableRestoreFocus
                disableEnforceFocus
                keepMounted
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this project?
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteId(null)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={actionLoading}
                    >
                        {actionLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
